import { isNil } from 'lodash'
import { useFetchExplanationQuery } from '../../submission.api'
import { useAnswerContext } from '../../../../pages/assignment/components/text-submission-drawer/content/answers/answer/AnswerContext'
import { useSelector } from 'react-redux'
import assignmentSelectors from '../../../selectors/assignment.selectors'
import { isMba } from '../../../../utils/assignment.helper'
import textSubmissionDrawerSelectors from '../../../selectors/text-submission-drawer.selectors'
import { useRef, useCallback } from 'react'
import useRubricContext from '../../../../hooks/useRubricContext'

export const useHoverPrefetch = (answer) => {
    const { setCategoryIdToPrefetch } = useAnswerContext()
    const timeoutRef = useRef(null)
    const focusedQuestionRubricId = useSelector(textSubmissionDrawerSelectors.focusedQuestionRubricId)
    const type = useSelector(assignmentSelectors.type)

    const { questionRubric, categories } = useRubricContext({ questionRubricId: answer?.questionRubricId })

    // Check if we should skip prefetching for a given category
    const shouldSkipPrefetch = useCallback((categoryId) => {
        return !answer ||
            isNil(categoryId) ||
            categoryId === answer?.categoryId ||
            focusedQuestionRubricId !== answer?.questionRubricId ||
            (!isMba(type) && !questionRubric?.lastTrained) ||
            !categories?.some(category => category.id === categoryId)
    }, [answer, answer?.categoryId, answer?.questionRubricId, focusedQuestionRubricId, type, questionRubric, categories])

    // This will prefetch and cache the explanation
    const { categoryIdToPrefetch } = useAnswerContext()
    useFetchExplanationQuery(
        {
            answerId: answer?.id,
            submissionId: answer?.submissionId,
            categoryId: categoryIdToPrefetch,
        },
        {
            skip: shouldSkipPrefetch(categoryIdToPrefetch),
        },
    )

    const handleMouseEnter = useCallback((categoryId) => {
        // Skip if we shouldn't prefetch this category
        if (shouldSkipPrefetch(categoryId)) {
            return
        }

        // Clear any existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        // Set a new timeout for 300ms
        timeoutRef.current = setTimeout(() => {
            setCategoryIdToPrefetch(categoryId)
        }, 300)
    }, [setCategoryIdToPrefetch, shouldSkipPrefetch])

    const handleMouseLeave = useCallback(() => {
        // Clear timeout if user stops hovering before 200ms
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        
        // Clear prefetch after a short delay to avoid flickering
        setTimeout(() => {
            setCategoryIdToPrefetch(null)
        }, 100)
    }, [setCategoryIdToPrefetch])

    return { handleMouseEnter, handleMouseLeave }
}

export const useExplanation = answer => {
    const focusedQuestionRubricId = useSelector(textSubmissionDrawerSelectors.focusedQuestionRubricId)
    const { categoryIdToHighlight } = useAnswerContext()
    const type = useSelector(assignmentSelectors.type)

    const { questionRubric, categories } = useRubricContext({ questionRubricId: answer?.questionRubricId })

    // Skip fetching explanation if:
    // 0. No answer exists (e.g., question has no rubrics)
    // 1. No category is highlighted to compare against
    // 2. The highlighted category is the same as the answer's current category (no change)
    // 3. This answer is not from the currently focused questionRubric
    // 4. For non-MBA assignments, skip if the questionRubric hasn't been trained yet
    // 5. The highlighted category is not in the current questionRubric's categories
    const skip =
        !answer ||
        isNil(categoryIdToHighlight) ||
        categoryIdToHighlight === answer?.categoryId ||
        focusedQuestionRubricId !== answer?.questionRubricId ||
        (!isMba(type) && !questionRubric?.lastTrained) ||
        !categories?.some(category => category.id === categoryIdToHighlight)

    const { data: explanation, isFetching } = useFetchExplanationQuery(
        {
            answerId: answer?.id,
            categoryId: categoryIdToHighlight,
        },
        {
            skip,
        },
    )

    return {
        explanation: skip || isFetching ? null : explanation,
        isFetching,
        categoryId: categoryIdToHighlight,
    }
}
